import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../api/client.js';

// Reports that can have a contact list assigned as their recipient source.
// Extend this (and the matching ASSIGNABLE_REPORTS on the server) when a new
// report grows a real, broadcast-style distribution list.
const REPORTS = [
  { value: 'usage_digest', label: 'CRM usage digest (10am/1pm/4pm Pacific)' },
];

const SALES_FUNCTIONS = [
  { value: 'bdm', label: 'BDM' },
  { value: 'bdc', label: 'BDC' },
  { value: 'aftermarket', label: 'Aftermarket' },
  { value: 'equipment', label: 'Equipment' },
];

export default function ContactLists() {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState(null); // group id or 'new'
  const [newForm, setNewForm] = useState({ name: '', description: '' });

  const list = useQuery({
    queryKey: ['admin-email-groups'],
    queryFn: () => api.get('/admin/email-groups').then(r => r.data.data),
  });
  const assignments = useQuery({
    queryKey: ['admin-report-group-assignments'],
    queryFn: () => api.get('/admin/report-group-assignments').then(r => r.data.data),
  });

  const create = useMutation({
    mutationFn: (data) => api.post('/admin/email-groups', data).then(r => r.data.data),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['admin-email-groups'] });
      setNewForm({ name: '', description: '' });
      setEditingId(row.id);
    },
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/admin/email-groups/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-email-groups'] });
      qc.invalidateQueries({ queryKey: ['admin-report-group-assignments'] });
      setEditingId(null);
    },
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <div className="section-eyebrow">Admin</div>
          <h1 className="text-2xl">Contact lists</h1>
          <div className="rule-crimson" />
          <p className="text-sm text-muted mt-1">
            Superadmin-only. Named recipient lists — assign one to a report below to control who
            receives it, no code deploy needed. The 6am follow-up digest and daily standup are
            controlled per-user instead (Users → Sales function / digest opt-in).
          </p>
        </div>
        <button className="btn-primary" onClick={() => setEditingId('new')}>+ New list</button>
      </header>

      {editingId === 'new' && (
        <div className="card p-3 space-y-2">
          <div className="font-medium text-navy">New contact list</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input className="input" placeholder="Name (e.g. Management Team)"
                   value={newForm.name} onChange={e => setNewForm({ ...newForm, name: e.target.value })} />
            <input className="input" placeholder="Description (optional)"
                   value={newForm.description} onChange={e => setNewForm({ ...newForm, description: e.target.value })} />
          </div>
          {create.isError && <div className="text-crimson text-sm">{create.error?.response?.data?.error || 'Failed'}</div>}
          <div className="flex justify-end gap-2">
            <button className="btn-ghost" onClick={() => setEditingId(null)}>Cancel</button>
            <button className="btn-primary" disabled={create.isPending || !newForm.name.trim()}
                    onClick={() => create.mutate(newForm)}>
              {create.isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {list.isLoading && <div className="text-muted">Loading…</div>}

      <div className="space-y-2">
        {(list.data || []).map(g => (
          <div key={g.id} className="card p-3">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-navy">{g.name}</div>
                {g.description && <div className="text-xs text-muted">{g.description}</div>}
                <div className="text-xs text-muted mt-0.5">
                  {g.member_count} member{g.member_count === '1' ? '' : 's'}
                  {g.assigned_reports?.length > 0 && (
                    <> · used by {g.assigned_reports.map(r => REPORTS.find(x => x.value === r)?.label || r).join(', ')}</>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <button className="btn-ghost text-xs" onClick={() => setEditingId(editingId === g.id ? null : g.id)}>
                  {editingId === g.id ? 'Close' : 'Edit'}
                </button>
                <button className="btn-ghost text-xs" disabled={remove.isPending}
                        onClick={() => { if (confirm(`Delete "${g.name}"? This also un-assigns it from any reports.`)) remove.mutate(g.id); }}>
                  Delete
                </button>
              </div>
            </div>
            {editingId === g.id && (
              <GroupDetail groupId={g.id} assignments={assignments.data || []} />
            )}
          </div>
        ))}
        {list.data?.length === 0 && !list.isLoading && (
          <div className="card p-3 text-muted text-sm text-center">No contact lists yet.</div>
        )}
      </div>
    </div>
  );
}

function GroupDetail({ groupId, assignments }) {
  const qc = useQueryClient();
  const [memberForm, setMemberForm] = useState({ member_type: 'email', value: '', recipient_type: 'to', label: '' });

  const detail = useQuery({
    queryKey: ['admin-email-group', groupId],
    queryFn: () => api.get(`/admin/email-groups/${groupId}`).then(r => r.data.data),
  });

  const addMember = useMutation({
    mutationFn: (data) => api.post(`/admin/email-groups/${groupId}/members`, data).then(r => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-email-group', groupId] });
      qc.invalidateQueries({ queryKey: ['admin-email-groups'] });
      setMemberForm(f => ({ ...f, value: '', label: '' }));
    },
  });
  const removeMember = useMutation({
    mutationFn: (memberId) => api.delete(`/admin/email-groups/${groupId}/members/${memberId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-email-group', groupId] });
      qc.invalidateQueries({ queryKey: ['admin-email-groups'] });
    },
  });

  const assign = useMutation({
    mutationFn: (report) => api.post('/admin/report-group-assignments', { report, group_id: groupId }).then(r => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-report-group-assignments'] });
      qc.invalidateQueries({ queryKey: ['admin-email-groups'] });
    },
  });
  const unassign = useMutation({
    mutationFn: (assignmentId) => api.delete(`/admin/report-group-assignments/${assignmentId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-report-group-assignments'] });
      qc.invalidateQueries({ queryKey: ['admin-email-groups'] });
    },
  });

  const groupAssignments = assignments.filter(a => a.group_id === groupId);

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
      {detail.isLoading && <div className="text-muted text-sm">Loading…</div>}
      {detail.data && (
        <>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted mb-1">Members</div>
            {detail.data.members.length === 0 && <div className="text-sm text-muted">No members yet.</div>}
            {['to', 'cc'].map(rt => {
              const rows = detail.data.members.filter(m => m.recipient_type === rt);
              if (!rows.length) return null;
              return (
                <div key={rt} className="mb-1">
                  <div className="text-[10px] text-muted uppercase">{rt}</div>
                  {rows.map(m => (
                    <div key={m.id} className="flex items-center justify-between gap-2 py-1 border-b border-gray-100 last:border-0">
                      <div className="text-sm">
                        {m.member_type === 'role' ? (
                          <span className="font-medium">All active {SALES_FUNCTIONS.find(s => s.value === m.value)?.label || m.value} reps</span>
                        ) : (
                          <>
                            <span className="font-medium">{m.label || m.value}</span>
                            {m.label && <span className="text-muted"> · {m.value}</span>}
                          </>
                        )}
                      </div>
                      <button className="btn-ghost text-xs" disabled={removeMember.isPending}
                              onClick={() => removeMember.mutate(m.id)}>Remove</button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-muted mb-1">Add member</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <select className="input" value={memberForm.member_type}
                      onChange={e => setMemberForm({ ...memberForm, member_type: e.target.value, value: '' })}>
                <option value="email">Email</option>
                <option value="role">Sales function (role rule)</option>
              </select>
              <select className="input" value={memberForm.recipient_type}
                      onChange={e => setMemberForm({ ...memberForm, recipient_type: e.target.value })}>
                <option value="to">To</option>
                <option value="cc">CC</option>
              </select>
              {memberForm.member_type === 'email' ? (
                <>
                  <input className="input" type="email" placeholder="email@utscc.com"
                         value={memberForm.value} onChange={e => setMemberForm({ ...memberForm, value: e.target.value })} />
                  <input className="input" placeholder="Name (optional)"
                         value={memberForm.label} onChange={e => setMemberForm({ ...memberForm, label: e.target.value })} />
                </>
              ) : (
                <select className="input col-span-2" value={memberForm.value}
                        onChange={e => setMemberForm({ ...memberForm, value: e.target.value })}>
                  <option value="">— pick a sales function —</option>
                  {SALES_FUNCTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              )}
            </div>
            {addMember.isError && <div className="text-crimson text-sm mt-1">{addMember.error?.response?.data?.error || 'Failed'}</div>}
            <button className="btn-primary text-sm mt-2" disabled={addMember.isPending || !memberForm.value}
                    onClick={() => addMember.mutate(memberForm)}>
              {addMember.isPending ? 'Adding…' : '+ Add member'}
            </button>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-muted mb-1">Used by report</div>
            <div className="space-y-1">
              {REPORTS.map(r => {
                const assignment = groupAssignments.find(a => a.report === r.value);
                return (
                  <label key={r.value} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!assignment}
                           disabled={assign.isPending || unassign.isPending}
                           onChange={() => assignment ? unassign.mutate(assignment.id) : assign.mutate(r.value)} />
                    {r.label}
                  </label>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
